import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtDate, fmtDateShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardList, Printer, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { value: "paint_out", label: "Paint — going out" },
  { value: "paint_in", label: "Paint — returning" },
  { value: "delivery", label: "Delivery" },
];

export function Manifests() {
  const [list, setList] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);

  // form
  const [type, setType] = useState("paint_out");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [driver, setDriver] = useState("");
  const [note, setNote] = useState("");
  const [picked, setPicked] = useState({}); // key `${oid}:${iid}` -> bool

  const load = async () => {
    const { data } = await api.get("/manifests");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (open && orders.length === 0) {
      api.get("/orders").then(({ data }) => setOrders(data));
    }
  }, [open, orders.length]);

  const filteredItems = () => {
    const rows = [];
    for (const o of orders) {
      for (const it of o.items) {
        if (type === "paint_out" && it.paint_status === "awaiting") {
          rows.push({ o, it });
        } else if (type === "paint_in" && it.paint_status === "returned") {
          rows.push({ o, it });
        } else if (type === "delivery") {
          rows.push({ o, it });
        }
      }
    }
    return rows;
  };

  const submit = async () => {
    const refs = Object.entries(picked)
      .filter(([, v]) => v)
      .map(([k]) => {
        const [order_id, item_id] = k.split(":");
        return { order_id, item_id };
      });
    if (refs.length === 0) { toast.error("Pick at least one item"); return; }
    try {
      await api.post("/manifests", { type, date, driver: driver || null, note: note || null, item_refs: refs });
      toast.success("Manifest created");
      setOpen(false);
      setPicked({});
      setDriver("");
      setNote("");
      await load();
    } catch {
      toast.error("Failed to create manifest");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="label-eyebrow">Manifests</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Paint & delivery manifests
          </h1>
          <p className="text-muted-foreground mt-1">
            Group items into a printable run sheet for the paint shop or driver.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-manifest-btn" className="bg-primary hover:bg-[#203A2C]">
              <Plus className="w-4 h-4 mr-2" />
              New manifest
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>Create manifest</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={type} onValueChange={(v) => { setType(v); setPicked({}); }}>
                    <SelectTrigger data-testid="manifest-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`manifest-type-${o.value}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="manifest-date" />
                </div>
                <div>
                  <Label>Driver</Label>
                  <Input value={driver} onChange={(e) => setDriver(e.target.value)} placeholder="Optional" data-testid="manifest-driver" />
                </div>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional instructions" data-testid="manifest-note" />
              </div>

              <div>
                <div className="label-eyebrow mb-2">Eligible items</div>
                <div className="max-h-72 overflow-auto border border-border/60 rounded-md divide-y divide-border/40">
                  {filteredItems().length === 0 && (
                    <div className="p-6 text-sm text-muted-foreground text-center">
                      No items match this manifest type.
                    </div>
                  )}
                  {filteredItems().map(({ o, it }) => {
                    const k = `${o.id}:${it.id}`;
                    return (
                      <label key={k} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/40 cursor-pointer">
                        <Checkbox
                          checked={!!picked[k]}
                          onCheckedChange={(v) => setPicked((p) => ({ ...p, [k]: !!v }))}
                          data-testid={`manifest-pick-${it.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{it.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {o.number} · {o.customer.name} · Qty {it.qty}
                            {it.paint_color ? ` · ${it.paint_color}` : ""}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <Button onClick={submit} data-testid="manifest-submit" className="w-full bg-primary hover:bg-[#203A2C]">
                Create manifest
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="surface-card p-10 text-center text-muted-foreground">
          No manifests yet. Click &ldquo;New manifest&rdquo; to create your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((m) => (
            <Link
              key={m.id}
              to={`/manifests/${m.id}`}
              data-testid={`manifest-card-${m.number}`}
              className="surface-card p-5 hover:shadow-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-primary">{m.number}</span>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.type.replace("_", " ")}</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{fmtDateShort(m.date)} · {m.items.length} items</span>
              </div>
              {m.driver && <div className="text-xs text-muted-foreground">Driver: {m.driver}</div>}
              <div className="text-xs text-muted-foreground mt-2">Created {fmtDateShort(m.created_at)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ManifestDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get(`/manifests/${id}`).then(({ data }) => setM(data));
  }, [id]);

  if (!m) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] animate-fade-in">
      <Link to="/manifests" className="no-print inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Manifests
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="label-eyebrow">{m.type.replace("_", " ")} manifest</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-2 font-mono">{m.number}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {fmtDate(m.date)} {m.driver && `· Driver: ${m.driver}`}
          </div>
          {m.note && <div className="text-sm mt-2 italic">{m.note}</div>}
        </div>
        <Button
          onClick={() => window.print()}
          variant="outline"
          data-testid="print-manifest"
          className="no-print"
        >
          <Printer className="w-4 h-4 mr-2" /> Print
        </Button>
      </div>

      <div className="surface-card p-6">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border/70">
              <th className="text-left font-medium px-2 py-3">Order</th>
              <th className="text-left font-medium px-2 py-3">Customer</th>
              <th className="text-left font-medium px-2 py-3">Item</th>
              <th className="text-right font-medium px-2 py-3">Qty</th>
              <th className="text-left font-medium px-2 py-3">Color</th>
              <th className="text-left font-medium px-2 py-3 hidden md:table-cell">Address</th>
            </tr>
          </thead>
          <tbody>
            {m.items.map((it, i) => (
              <tr key={i} className="border-b border-border/40">
                <td className="px-2 py-3 font-mono">{it.order_number}</td>
                <td className="px-2 py-3">{it.customer}</td>
                <td className="px-2 py-3">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{it.sku}</div>
                </td>
                <td className="px-2 py-3 text-right num">{it.qty}</td>
                <td className="px-2 py-3 text-[#a44231]">{it.color || "—"}</td>
                <td className="px-2 py-3 hidden md:table-cell text-muted-foreground">{it.address || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 border-t border-border/70 pt-4 text-sm grid grid-cols-2 gap-4">
          <div>
            <div className="label-eyebrow">Driver signature</div>
            <div className="mt-12 border-b border-border" />
          </div>
          <div>
            <div className="label-eyebrow">Receiving signature</div>
            <div className="mt-12 border-b border-border" />
          </div>
        </div>
      </div>
    </div>
  );
}
